import { MetaTransactionData, OperationType } from '@safe-global/types-kit'
import type { Abi } from 'viem'
import { encodeFunctionData } from 'viem'

import abiVestingV2Json from 'abi/abi-vesting-v2.json'

const abiVestingV2 = abiVestingV2Json as Abi
import { ENABLE_TESTNET } from 'constants/index'

import { SafeClients } from './client'

const SAFE_APP_BASE_URL = 'https://app.safe.global'

export const buildSafeTransactionUrl = (safeAddress: string, safeTxHash: string): string => {
  const shortName = ENABLE_TESTNET ? 'basesep' : 'base'
  const safeParam = `${shortName}:${safeAddress}`
  return `${SAFE_APP_BASE_URL}/transactions/tx?safe=${safeParam}&id=multisig_${safeAddress}_${safeTxHash}`
}

interface ProposeBatchParams {
  clients: SafeClients
  safeAddress: `0x${string}`
  signerAddress: `0x${string}`
  vestingContractAddress: `0x${string}`
  chunk: {
    users: `0x${string}`[]
    signDates: number[]
    amounts: bigint[]
  }
  nonce?: number
}

export interface ProposedBatchResult {
  safeTxHash: string
  safeWebUrl: string
  nonce: number
  confirmations: number
  confirmationsRequired: number
}

export const proposeBatchTransaction = async ({
  clients,
  safeAddress,
  signerAddress,
  vestingContractAddress,
  chunk,
  nonce,
}: ProposeBatchParams): Promise<ProposedBatchResult> => {
  const { protocolKit, apiKit } = clients

  const data = encodeFunctionData({
    abi: abiVestingV2,
    functionName: 'addVestingEntries',
    args: [chunk.users, chunk.signDates.map((s) => BigInt(s)), chunk.amounts],
  })

  const safeTransactionData: MetaTransactionData = {
    to: vestingContractAddress,
    value: '0',
    data,
    operation: OperationType.Call,
  }

  const safeTransaction = await protocolKit.createTransaction({
    transactions: [safeTransactionData],
    options: nonce !== undefined ? { nonce } : undefined,
  })

  const safeTxHash = await protocolKit.getTransactionHash(safeTransaction)
  const signature = await protocolKit.signHash(safeTxHash)

  await apiKit.proposeTransaction({
    safeAddress,
    safeTransactionData: safeTransaction.data,
    safeTxHash,
    senderAddress: signerAddress,
    senderSignature: signature.data,
  })

  const confirmationsRequired = await protocolKit.getThreshold()

  const safeWebUrl = buildSafeTransactionUrl(safeAddress, safeTxHash)

  return {
    safeTxHash,
    safeWebUrl,
    nonce: Number(safeTransaction.data.nonce),
    confirmations: 1,
    confirmationsRequired,
  }
}
